/**
 * API Client Setup
 * Axios instance with authentication and error handling
 */

import axios, { AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { ApiError } from './types';
import logger from '../utils/logger';

// API Base URL - configurable via environment variable
// Expo public env vars must start with EXPO_PUBLIC_
const API_BASE_URL = process.env.EXPO_PUBLIC_API_URL || (
  __DEV__
    ? 'http://localhost:3000/api/v0'
    : 'https://api-staging.wealfi.co/api/v0'
);

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: 30000, // 30 seconds for AI responses
});

// Request interceptor: Add auth token
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const startTime = Date.now();
    try {
      const tokenStart = Date.now();
      const token = await AsyncStorage.getItem('access_token');
      const tokenTime = Date.now() - tokenStart;

      logger.log(`[API] Token retrieval took ${tokenTime}ms for ${config.url}`);

      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      logger.error('Error getting token from storage:', error);
    }

    const totalTime = Date.now() - startTime;
    logger.log(`[API] Request interceptor took ${totalTime}ms for ${config.url}`);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor: Handle errors
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log response time if request config has timing info
    if (response.config && response.config.url) {
      logger.log(`[API] Response received for ${response.config.url}`);
    }
    return response;
  },
  async (error: AxiosError<ApiError>) => {
    // Log 401 errors but do NOT auto-remove tokens.
    // Token cleanup is handled by authStore.logout() only.
    // Auto-removing tokens on any 401 causes cascading logouts
    // (e.g., a single failed request wipes auth for the entire session).
    if (error.response?.status === 401) {
      logger.warn(`[API] 401 Unauthorized for ${error.config?.url}`);
    }

    // Return error with formatted structure
    return Promise.reject(error);
  }
);

export default apiClient;

